/**
 * RD-EST-RESP-001 — the two `company_settings.settings` keys this feature owns,
 * and the pure diffs that turn a save into an audit row.
 *
 * *** EXTRACTED FROM `companySettingsService.js`, WHICH WAS AT 489 LINES. ***
 * The 500-line rule says extract before adding, and this is the natural seam:
 * everything here is pure, has no Supabase import, and is shared by the service,
 * the settings UI and the tests. The service re-exports both keys so no existing
 * import path breaks.
 *
 * *** BOTH KEYS ARE CROSS-RUNTIME CONTRACTS, WHICH IS WHY THEY ARE snake_case
 * WHILE EVERY SIBLING SETTING IS camelCase. *** They are read on the server by
 * `supabase/functions/_shared/estimateResponseState.ts`
 * (`ESTIMATE_RESPONSES_SETTINGS_KEY` / `ESTIMATE_RESPONSE_NOTIFY_SETTINGS_KEY`),
 * so the stored spelling is part of the wire format, not a local style choice.
 * Rename one side only and the failure is silent in the worst way: the UI keeps
 * showing the tenant's configuration while the server acts on the old one.
 */

/** The tenant switch: may customers approve/decline from the emailed link? */
export const ESTIMATE_RESPONSES_SETTINGS_KEY = 'customer_estimate_responses';

/**
 * The "also notify" list: EXTRA user ids that receive the response notification
 * on top of the estimate's sender and the tenant owner.
 *
 * *** IT ADDS; IT NEVER SUBTRACTS. *** The floor (sender + owner) is resolved
 * server-side and independently, so an empty list is not "notify nobody" — it is
 * "notify the two people who always get it". That asymmetry is the whole safety
 * argument for shipping a configurable recipient list at all: the failure mode
 * of a list that CAN be emptied is a customer approving a job and nobody being
 * told, which looks identical to the feature being broken and costs real money
 * before anyone notices.
 *
 * USER IDS, never emails — see the server-side twin's comment for why.
 */
export const ESTIMATE_RESPONSE_NOTIFY_SETTINGS_KEY = 'estimate_response_notify_user_ids';

/**
 * Mirrors `MAX_NOTIFY_EXTRA_USER_IDS` in estimateResponseState.ts. The server's
 * copy is the one that protects the owner from being truncated out of the
 * recipient query; this one just stops the UI from writing a value the server
 * would silently trim, so the picker and the mail agree on what was saved.
 */
export const MAX_NOTIFY_USER_IDS = 20;

const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/**
 * The CANONICAL stored shape: uuid-shaped strings, lowercased, deduped, sorted,
 * capped.
 *
 * Sorting is not cosmetic. Both dirty-detection paths that decide whether the
 * Settings save bar appears (`useBrandingHandlers`'s `isDirty` and its
 * `seededRef` pristine guard) are `JSON.stringify` compares over the whole form,
 * so an array whose ORDER depended on click sequence would read as an unsaved
 * edit after ticking and unticking the same person. A canonical order makes
 * "the same set" literally the same string.
 *
 * Fails to `[]` on every malformed input, matching the server reader exactly:
 * the floor is unaffected, so a bad value degrades to the pre-picker behaviour
 * instead of to silence.
 */
export function normalizeNotifyUserIds(value) {
  if (!Array.isArray(value)) return [];
  const out = new Set();
  for (const entry of value) {
    if (typeof entry !== 'string') continue;
    const trimmed = entry.trim();
    if (!UUID_RE.test(trimmed)) continue;
    out.add(trimmed.toLowerCase());
    if (out.size >= MAX_NOTIFY_USER_IDS) break;
  }
  return [...out].sort();
}

/**
 * Did THIS save flip the estimate-response switch? Pure so the caller stays
 * readable and the boolean coercion lives in one place.
 *
 * Both sides are normalized with `=== true` on purpose: the stored value can be
 * absent (never set), `''` (the sanitizer's fallback for a key with no default
 * in `branding`), or a real boolean. Only `true` is ON — every other shape is
 * OFF, exactly like the server's `readEstimateResponsesToggle`. Without the
 * normalization, an untouched tenant's first save would compare `undefined`
 * against `''` and emit a phantom "policy changed" row.
 */
export function diffEstimateResponsesToggle(prior, next) {
  const before = prior?.[ESTIMATE_RESPONSES_SETTINGS_KEY] === true;
  const after = next?.[ESTIMATE_RESPONSES_SETTINGS_KEY] === true;
  return before === after ? null : { previous: before, enabled: after };
}

/**
 * Did THIS save change WHO ELSE gets told?
 *
 * Normalized on both sides before comparing, so the same set written in a
 * different order (or with a stray blank) is not audited as a change — the
 * Activity Log is read by someone asking "who added Carla to the estimate
 * notifications, and when", and a stream of no-op rows is how that question
 * stops being answerable.
 *
 * Returns ids, not names: the audit payload must stay stable when someone is
 * renamed or leaves, and the renderer resolves ids against the current roster.
 */
export function diffEstimateResponseNotifyUsers(prior, next) {
  const before = normalizeNotifyUserIds(prior?.[ESTIMATE_RESPONSE_NOTIFY_SETTINGS_KEY]);
  const after = normalizeNotifyUserIds(next?.[ESTIMATE_RESPONSE_NOTIFY_SETTINGS_KEY]);
  if (before.join('|') === after.join('|')) return null;
  return {
    previous: before,
    user_ids: after,
    added: after.filter((id) => !before.includes(id)),
    removed: before.filter((id) => !after.includes(id)),
  };
}
